import React, { useEffect, useState } from 'react';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import apiService from 'services/apiService';

// helper functions
const sortLawListByDate = (laws) => {
  return laws.sort((a, b) => {
    const aDate = new Date(a?.data?.date);
    const bDate = new Date(b?.data?.date);
    return bDate - aDate;
  });
};

const LawHistoryBox = ({ pageData, setIsLoading }) => {
  const [boxData, setBoxData] = useState([]);

  useEffect(() => {
    // fetch data, convert it into usable objects,
    // and save to state
    const fetchLawHistory = async () => {
      try {
        return Promise.all(
          pageData.historyBoxLaws.map(async (law) => {
            const [law_id, year] = law.id.split('/');
            return apiService
              .fetchData(`lawhistory/${year}/${law_id}`)
              .catch((err) => {
                console.warn(
                  'Error while fetching',
                  law.id + ':',
                  err,
                );
                return null;
              });
          }),
        ).then((values) => {
          return (
            values
              // filter out nulls
              .filter((item) => item)
              // flatten by removing unnecessary nesting
              .map((item) => item.response)
          );
        });
      } catch (err) {
        console.error(err);
        return [];
      }
    };

    // async returns a Promise, so use then
    fetchLawHistory()
      .then((data) => {
        const sortedData = data.map((law) => {
          const { amended_by, repeals } = law;
          return {
            ...law,
            amended_by: sortLawListByDate(amended_by),
            repeals: sortLawListByDate(repeals),
          };
        });
        setBoxData(sortedData);
      })
      .finally(() => setIsLoading(false));

    // cleanup on unmount
    return () => {
      setBoxData([]);
    };
  }, [pageData]);

  const getLawNameById = (id) => {
    if (!id) return;

    return pageData?.historyBoxLaws?.find(
      (law) => law.id === id,
    ).lawName;
  };

  return boxData.map(
    ({ id, amended_by, repeals }) =>
      (amended_by.length > 0 || repeals.length > 0) && (
        <div key={id} className="smaller-font">
          <Accordion
            titleElement={
              <p className="accordion-title accordion-main-title">
                {getLawNameById(id)} {id}
              </p>
            }
            initialIsOpen={false}
          >
            {amended_by.length > 0 && (
              <Accordion
                titleElement={
                  <p className="accordion-title">
                    Muutokset
                  </p>
                }
              >
                {amended_by.map(({ id, note, data }) => (
                  <HistoryEntry
                    key={id}
                    id={id}
                    data={data}
                    note={note}
                  />
                ))}
              </Accordion>
            )}
            {repeals.length > 0 && (
              <Accordion
                titleElement={
                  <p className="accordion-title">
                    Kumonnut säädökset
                  </p>
                }
              >
                {repeals.map(({ id, data }) => (
                  <HistoryEntry
                    key={id}
                    id={id}
                    data={data}
                  />
                ))}
              </Accordion>
            )}
          </Accordion>
        </div>
      ),
  );
};

const HistoryEntry = ({ id, data, note = null }) => {
  // a single law's date, title, id, and possible note
  return (
    <div className="history-entry">
      {data?.date && (
        <span className="history-date">
          {new Date(data?.date).toDateString()}
        </span>
      )}
      <LawTitleWithLink id={id} data={data} />
      {note && <span className="history-note">{note}</span>}
    </div>
  );
};

const LawTitleWithLink = ({ id, data }) => {
  // there is (currently) no way of figuring out whether
  // a law is a part of the Up-to-date Legislation (ajantasaistettu lainsäädäntö)
  // or the Statute Book of Finland (säädöskokoelma) collection of Finlex
  // One law uses finlex.fi/fi/lainsaadanto/1889/36 and
  // another uses .../lainsaadanto/saadoskokoelma/2026/125.
  // just use Finlex' search functionality to find the documents...
  const href = data?.he_id
    ? `/asiakirjat/hallituksen_esitykset/${data.he_id}`
    : null;
  const hasTitle =
    data !== undefined && data.title !== undefined;

  return (
    <span>
      {hasTitle && data.title + ' ('}
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {id}
        </a>
      ) : (
        id
      )}
      {hasTitle && ')'}
    </span>
  );
};

const Accordion = ({
  titleElement,
  children,
  initialIsOpen = true,
}) => {
  // clicking the title element toggles the visibility of the child content

  const [isOpen, setIsOpen] = useState(initialIsOpen);

  const toggleIsOpen = () => setIsOpen(!isOpen);

  return (
    <div className="history-accordion">
      <div className="accordion-title-wrapper">
        {/* arrow element */}
        {isOpen ? (
          <KeyboardArrowUp
            className="accordion-toggle-btn"
            onClick={toggleIsOpen}
          />
        ) : (
          <KeyboardArrowDown
            className="accordion-toggle-btn"
            onClick={toggleIsOpen}
          />
        )}
        {titleElement}
      </div>
      {isOpen && children}
    </div>
  );
};

export default LawHistoryBox;
